import React, { useEffect, useState } from 'react';
import { FiEye, FiTrash2, FiEdit2, FiSearch } from 'react-icons/fi';
import { listOrdersAllUsers, updateOrderStatus, deleteSubDoc } from '../services/dbService';
import toast from 'react-hot-toast';
import './Orders.css';

const Orders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingOrder, setEditingOrder] = useState(null);
    const [formData, setFormData] = useState({
        status: 'pending',
    });
    const [updating, setUpdating] = useState(false);

    const statusOptions = ['pending', 'confirmed', 'cancelled', 'completed'];

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const response = await listOrdersAllUsers();
            // normalize Firestore order shape to the UI's expected shape
            const normalized = response.map((o) => ({
                _id: o.id,
                userId: o.user_id || o.userId,
                userName: o.user_name || o.userName || 'N/A',
                userPhone: o.phone || o.userPhone || 'N/A',
                eventName: o.eventName || 'N/A',
                eventDate: o.event_date || 'N/A',
                eventTime: o.event_time || 'N/A',
                userEmail: o.userEmail || o.email || 'N/A',
                totalAmount: o.total_amount || o.totalAmount || o.total || 0,
                status: o.status || 'pending',
                createdAt: o.created_at || (o.createdAt && o.createdAt.toDate ? o.createdAt.toDate().toISOString() : (o.createdAt || new Date().toISOString())),
                items: o.items || [],
                orderNumber: o.orderNumber || o.id,
            }));
            setOrders(normalized);
        } catch (err) {
            console.error(err);
            toast.error('Failed to load orders');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (order) => {
        setSelectedOrder(order);
        setShowDetailModal(true);
    };

    const handleEditClick = (order) => {
        setEditingOrder(order);
        setFormData({
            status: order.status || 'pending',
        });
        setShowEditModal(true);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleUpdateOrder = async () => {
        if (!editingOrder) return;

        try {
            setUpdating(true);
            await updateOrderStatus(editingOrder.userId, editingOrder._id, formData);
            toast.success('Order status updated successfully');
            setShowEditModal(false);
            setEditingOrder(null);
            fetchOrders();
        } catch (err) {
            console.error(err);
            toast.error('Failed to update order');
        } finally {
            setUpdating(false);
        }
    };

    const handleDelete = async (id, userId) => {
        if (!window.confirm('Delete this order?')) return;
        try {
            await deleteSubDoc('users', userId, 'orders', id);
            toast.success('Order deleted');
            setOrders((prev) => prev.filter((o) => o._id !== id));
        } catch (err) {
            console.error(err);
            toast.error('Failed to delete order');
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'pending':
                return 'status-pending';
            case 'confirmed':
                return 'status-confirmed';
            case 'cancelled':
                return 'status-cancelled';
            case 'completed':
                return 'status-completed';
            default:
                return 'status-default';
        }
    };

    const filteredOrders = orders.filter((o) => {
        const term = searchTerm.toLowerCase();
        return (
            o._id?.toLowerCase().includes(term) ||
            o.orderNumber?.toLowerCase().includes(term) ||
            o.status?.toLowerCase().includes(term) ||
            o.userEmail?.toLowerCase().includes(term) ||
            o.userName?.toLowerCase().includes(term) ||
            o.userPhone?.toLowerCase().includes(term)
        );
    });

    if (loading) return <div className="orders-container"><p>Loading orders...</p></div>;

    return (
        <div className="orders-container animate-fade-in">
            <div className="orders-header">
                <h1>Orders Management</h1>
                <div className="search-box">
                    <FiSearch />
                    <input
                        type="text"
                        placeholder="Search orders..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="orders-table-wrapper">
                <table className="orders-table">
                    <thead>
                        <tr>
                            <th>Order ID</th>
                            <th>Ordered By</th>
                            <th>Event Name</th>
                            <th>Services</th>
                            <th>Order Date</th>
                            <th>Due Date</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredOrders.map((order) => (
                            <tr key={order._id}>
                                <td>{order.orderNumber || order._id.slice(-6).toUpperCase()}</td>
                                <td>
                                    <div>{order.userName}</div>
                                    <div style={{ fontSize: '0.85em', color: '#666' }}>{order.userPhone}</div>
                                </td>
                                <td>{order.eventName}</td>
                                <td>
                                    {order.items && order.items.length > 0 ? (
                                        <ul style={{ margin: 0, paddingLeft: '20px', listStyleType: 'disc' }}>
                                            {order.items.map((item, idx) => (
                                                <li key={idx} style={{ marginBottom: '4px' }}>
                                                    <strong>{item.service_title || item.serviceDetails?.name || 'Unknown'}</strong>
                                                    <br />
                                                    {item.serviceDetails?.vendor ? (
                                                        <span style={{ fontSize: '0.85em', color: '#555' }}>
                                                            Provider: {item.serviceDetails.vendor.businessName || item.serviceDetails.vendor.fullName || 'Unknown'} ({item.serviceDetails.vendor.phone || 'N/A'})
                                                        </span>
                                                    ) : null}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : 'No services'}
                                </td>
                                <td>
                                    {new Date(order.createdAt).toLocaleDateString()}
                                </td>
                                <td>
                                    {order.eventDate !== 'N/A' ? (
                                        <>
                                            <div>{order.eventDate}</div>
                                            <div style={{ fontSize: '0.85em', color: '#666' }}>{order.eventTime !== 'N/A' ? order.eventTime : ''}</div>
                                        </>
                                    ) : 'N/A'}
                                </td>
                                <td>{order.totalAmount?.toFixed(2) || '0.00'} ETB</td>
                                <td>
                                    <span className={`status-badge ${getStatusBadgeClass(order.status)}`}>
                                        {order.status}
                                    </span>
                                </td>
                                <td className="actions-cell">
                                    <button
                                        className="btn-view"
                                        onClick={() => handleViewDetails(order)}
                                        title="View Details"
                                    >
                                        <FiEye /> View
                                    </button>
                                    <button
                                        className="btn-edit"
                                        onClick={() => handleEditClick(order)}
                                        title="Edit Order"
                                    >
                                        <FiEdit2 /> Edit
                                    </button>
                                    <button
                                        className="btn-delete"
                                        onClick={() => handleDelete(order._id, order.userId)}
                                        title="Delete Order"
                                    >
                                        <FiTrash2 /> Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Detail Modal */}
            {showDetailModal && selectedOrder && (
                <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Order Details</h2>
                            <button
                                className="close-btn"
                                onClick={() => setShowDetailModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="detail-row">
                                <span className="detail-label">Order Number:</span>
                                <span className="detail-value">
                                    {selectedOrder.orderNumber || selectedOrder._id.slice(-6).toUpperCase()}
                                </span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Ordered By:</span>
                                <span className="detail-value">
                                    {selectedOrder.userName} ({selectedOrder.userPhone})
                                </span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Event Name:</span>
                                <span className="detail-value">
                                    {selectedOrder.eventName}
                                </span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Due Date:</span>
                                <span className="detail-value">
                                    {selectedOrder.eventDate !== 'N/A' ? `${selectedOrder.eventDate} ${selectedOrder.eventTime !== 'N/A' ? selectedOrder.eventTime : ''}` : 'N/A'}
                                </span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Total Amount:</span>
                                <span className="detail-value">
                                    {selectedOrder.totalAmount?.toFixed(2) || '0.00'} ETB
                                </span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Status:</span>
                                <span
                                    className={`detail-value status-badge ${getStatusBadgeClass(selectedOrder.status)}`}
                                >
                                    {selectedOrder.status}
                                </span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Order Date:</span>
                                <span className="detail-value">
                                    {new Date(selectedOrder.createdAt).toLocaleString()}
                                </span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Items Count:</span>
                                <span className="detail-value">
                                    {selectedOrder.items?.length || 0}
                                </span>
                            </div>
                            {selectedOrder.items && selectedOrder.items.length > 0 && (
                                <div className="detail-row">
                                    <span className="detail-label">Items:</span>
                                    <div className="detail-items">
                                        {selectedOrder.items.map((item, idx) => (
                                            <div key={idx} className="item-entry">
                                                - {item.service_title || item.serviceDetails?.name || item.name || item.title} x {item.quantity || 1} 
                                                {item.serviceDetails?.vendor && ` (Provider: ${item.serviceDetails.vendor.businessName || item.serviceDetails.vendor.fullName} - ${item.serviceDetails.vendor.phone})`}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button
                                className="btn-close"
                                onClick={() => setShowDetailModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && editingOrder && (
                <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="modal-content edit-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Edit Order Status</h2>
                            <button
                                className="close-btn"
                                onClick={() => setShowEditModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label htmlFor="status">Order Status</label>
                                <select
                                    id="status"
                                    name="status"
                                    value={formData.status}
                                    onChange={handleFormChange}
                                    className="form-input"
                                >
                                    {statusOptions.map((option) => (
                                        <option key={option} value={option}>
                                            {option.charAt(0).toUpperCase() + option.slice(1)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Order Number:</span>
                                <span className="detail-value">
                                    {editingOrder.orderNumber || editingOrder._id.slice(-6).toUpperCase()}
                                </span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Current Status:</span>
                                <span className={`detail-value status-badge ${getStatusBadgeClass(editingOrder.status)}`}>
                                    {editingOrder.status}
                                </span>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="btn-cancel"
                                onClick={() => setShowEditModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-submit"
                                onClick={handleUpdateOrder}
                                disabled={updating}
                            >
                                {updating ? 'Updating...' : 'Update Status'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Orders;
